package com.exura.pages.planPreview

import androidx.compose.runtime.Composable
import com.exura.components.buttons.AsyncButton
import com.exura.components.icons.InlineIcon
import com.exura.components.icons.UntiledIcon
import com.exura.components.layouts.plan.LayoutMode
import com.exura.components.layouts.plan.PlanConstructorHeaderElementStyle
import com.exura.components.layouts.plan.PlanConstructorLayoutScope
import com.exura.components.layouts.plan.SideBarPlanElement
import com.exura.components.pageTypography.ListEntryImageStyle
import com.exura.features.localization.commonLocalization
import com.exura.features.plandefinition.domain.model.element.BasePlanElement
import com.exura.features.plandefinition.domain.model.element.ElementType
import com.exura.features.plandefinition.planLocalization
import com.exura.navigation.Route
import com.exura.navigation.parameters.currentPageParams
import com.exura.ui.Assets
import com.exura.ui.Dimens
import com.exura.util.lineClamp
import com.varabyte.kobweb.browser.util.kebabCaseToTitleCamelCase
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier


@Composable
fun PreviewElementTooltipContent(
    spawnedElement: BasePlanElement,
    dayOffset: Int = 0,
    imageFallback: String? = null,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Dimens.planConstructorTooltipElementsGap),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        val imgSource = spawnedElement.imageUrl ?: imageFallback
        spawnedElement.relativeDay?.let { day ->
            SpanText("${planLocalization.current.day.kebabCaseToTitleCamelCase()} ${day + dayOffset}")
        }
        imgSource?.let {
            Box(
                modifier = ListEntryImageStyle.toModifier(),
                contentAlignment = Alignment.Center,
            ) {
                Image(it, modifier = Modifier.objectFit(ObjectFit.Contain).fillMaxSize())
            }
        }
        Row(
            modifier = Modifier
                .fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            when (spawnedElement.elementType) {
                ElementType.INSTRUCTION -> UntiledIcon(Assets.IconsUntiled.INSTRUCTION)
                ElementType.PERSISTENT -> UntiledIcon(Assets.IconsUntiled.PERSISTENT)
                ElementType.QUESTION -> UntiledIcon(Assets.IconsUntiled.QUESTION)
                ElementType.FLOATING -> UntiledIcon(Assets.IconsUntiled.FLOATING)
            }

            InlineIcon(
                icon = { UntiledIcon(Assets.IconsUntiled.DURATION) }
            ) {
                if (spawnedElement.durationInMinutes <= 24 * 60) {
                    SpanText(planLocalization.current.minutesCount(spawnedElement.durationInMinutes))
                } else {
                    SpanText(planLocalization.current.daysCount(spawnedElement.durationInDays()))
                }
            }
        }
        SpanText(
            spawnedElement.title,
            modifier = Modifier
                .fillMaxWidth()
                .lineClamp(3)
        )
    }
}

@Page(Route.WithParams.PLAN_PREVIEW_DETAILS)
@Layout(".components.layouts.plan.PlanConstructorLayout")
@Composable
fun PlanConstructorLayoutScope.PlanPreviewIndexPage() {
    val ctx = rememberPageContext()
    val planId = ctx.route.params[Route.Params.PLAN_ID] ?: return

    val showTooltipContent: @Composable (BasePlanElement, Int) -> Unit = { spawnedElement, dayOffset ->
        PreviewElementTooltipContent(spawnedElement, dayOffset = dayOffset, imageFallback = planBuilder.imageUrl())
    }

    val navigationStrategy: (SideBarPlanElement) -> Unit = { clickedElement ->
        // HANDLE LATER-ADDED / TEMPORAL IN THE FUTURE, NOW EXPECT THAT ALL ARE FROM DATABASE
        val basePath = when (clickedElement) {
            is SideBarPlanElement.RootSeriesItem -> Route.WithParams.toSeriesPreview(
                planId,
                null,
                clickedElement.seriesItem.firebaseDocId!!,
            )
            is SideBarPlanElement.CollectionItem -> Route.WithParams.toCollectionPreview(
                planId,
                clickedElement.collectionItem.firebaseDocId!!,
            )
            is SideBarPlanElement.NestedSeriesItem -> Route.WithParams.toSeriesPreview(
                planId,
                clickedElement.collectionItem.firebaseDocId!!,
                clickedElement.seriesItem.firebaseDocId!!,
            )
        }
        ctx.router.navigateTo("$basePath${currentPageParams()}")
    }

    setTooltipRenderStrategy(showTooltipContent)
    setInternalNavigationStrategy(navigationStrategy)
}
